<template>
  <div class="search search-listing">
    <div :class="{ 'search-sticky': sticky }" class="search-listing__wrapper">
      <div class="search-controls">
        <InputBox @click="toggle('destination')" class="search__input-box" icon="map"
          :label="selectedDestinationLabel" :value="selectedDestinationValue" :highlight="dropdowns.destination">
          <Icon v-if="selectedDestinations.length" slot="clear" class="search__input-box--clear" icon="cross"
            @click="clear(destinations, 'airports'); allDestinationsToggleSelected(false)" />
          <Destination v-if="dropdowns.destination" class="input-box__dropdown"
            v-model="destinations" :allSelected="allDestinationsSelected"
            @adjustVisibility="adjustDropdownVisibility"
            @input="toggle('destination')"
            @close="toggle('destination')"
            @allSelected="allDestinationsToggleSelected" />
        </InputBox>
        
        <InputBox @click="toggle('airport')" class="search__input-box" icon="v"
          :label="selectedAirportLabel" :value="selectedAirportValue" :highlight="dropdowns.airport">
          <Icon v-if="selectedAirports.length" slot="clear" class="search__input-box--clear" icon="cross"
            @click="clear(airports, 'destinations'); allAirportsToggleSelected(false)" />
          <Airport v-if="dropdowns.airport" class="input-box__dropdown"
            v-model="airports" :allSelected="allAirportsSelected"
            @adjustVisibility="adjustDropdownVisibility"
            @input="toggle('airport')"
            @close="toggle('airport')"
            @allSelected="allAirportsToggleSelected" />
        </InputBox>

        <DatesInputBox class="search__input-box" ref="datesInputBox"
          :label="selectedDateLabel"
          :start="selectedDateStartValue" :end="selectedDateEndValue"
          :highlight="dropdowns.dates ? dates.mode : ''"
          @click="startEditDates">

          <Calendar v-if="dropdowns.dates" ref="calendar"
            v-model="dates"
            :validDates="dates.validDates"
            :ranges="dates.ranges"
            :validRanges="dates.validRanges"
            :autoCloseParent="$refs.datesInputBox.$el"
            @adjustVisibility="adjustDropdownVisibility"
            @updateEditedDates="updateEditedDates"
            @close="endEditDates"
            @input="updateAvailableValues" />
        </DatesInputBox>

        <InputBox @click="toggle('duration')" class="search__input-box" icon="v"
          :label="selectedDurationLabel" :value="selectedDurationValue" :highlight="dropdowns.duration">
          <Duration v-if="dropdowns.duration" class="input-box__dropdown"
            v-model="durations"
            @adjustVisibility="adjustDropdownVisibility"
            @close="toggle('duration')" />
        </InputBox>

        <InputBox @click="toggle('people')" class="search__input-box" label="" icon="v"
          :value="selectedPeopleValue" :highlight="dropdowns.people">
          <People v-if="dropdowns.people" class="input-box__dropdown"
            v-model="people"
            @adjustVisibility="adjustDropdownVisibility($event, 70)"
            @input="toggle('people')"
            @close="toggle('people')" />
        </InputBox>

        <button class="search__submit" role="button" @click="search">
          Szukaj
          <Icon icon="v" />
        </button>
      </div>

      <button class="search-filters--toggle" :class="{ 'search-filters--selected': selectedFiltersCount }" @click="toggle('filters')">
        Więcej filtrów <span v-if="selectedFiltersCount">({{ selectedFiltersCount }})</span>
        <Icon icon="v" />
      </button>

      <ModalRoot v-if="dropdowns.filters" title="Filtrowanie wyników wyszukiwania" class="search-filters__dialog"
        :closeByClickingOutside="true" @close="toggle('filters')">
        <div class="search-filters">
          <PricePerPerson v-model="pricePerPerson.selected" :options="pricePerPerson.options" />
          
          <BoardStandard v-model="boardStandards" />

          <HotelStandard v-model="hotelStandards" />

          <HotelCategories v-model="hotelCategories" />

          <Amenities v-model="amenities" />
          
          <OfferType v-model="offerTypes" />

          <Attractions v-model="attractions" />
        </div>

        <div class="search-filters__footer" slot="footer">
          <div class="search-filters__footer--clear" @click="clearFilters">
            <Icon icon="cross" />
            Wyczyść wszystko
          </div>
          <div class="search-filters__footer--select" @click="toggle('filters')">
            Wybierz
            <Icon icon="v-right" />
          </div>
        </div>
      </ModalRoot>
    </div>

    <div v-if="sticky" class="search-sticky--placeholder" />
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import Airport from './fields/AirportDropdown'
import Amenities from './fields/AmenitiesDropdown'
import Attractions from './fields/AttractionsDropdown'
import Beach from './fields/BeachDropdown'
import BoardStandard from './fields/BoardStandardDropdown'
import Destination from './fields/DestinationDropdown'
import Duration from './fields/DurationDropdown'
import ForChildren from './fields/ForChildrenDropdown'
import HotelCategories from './fields/HotelCategoryDropdown'
import HotelStandard from './fields/HotelStandardDropdown'
import OfferType from './fields/OfferTypeDropdown'
import People from './fields/PeopleDropdown'
import PricePerPerson from './fields/PricePerPersonDropdown'
import SportsEntertainment from './fields/SportsEntertainmentDropdown'
import InputBox from './InputBox.vue'
import AirportLogic from './logic/Airport'
import AmenitiesLogic from './logic/Amenities'
import AttractionsLogic from './logic/Attractions'
import BeachLogic from './logic/Beach'
import BoardStandardLogic from './logic/BoardStandard'
import DatesLogic from './logic/DatesDesktop'
import DestinationLogic from './logic/Destination'
import DurationLogic from './logic/Duration'
import ForChildrenLogic from './logic/ForChildren'
import HotelCategoriesLogic from './logic/HotelCategories'
import HotelStandardLogic from './logic/HotelStandard'
import OfferTypeLogic from './logic/OfferType'
import PeopleLogic from './logic/People'
import PricePerPersonLogic from './logic/PricePerPerson'
import SportsEntertainmentLogic from './logic/SportsEntertainment'

import DatesInputBox from '@/components/DatesInputBox'
import Icon from '@/components/Icon.vue'
import ModalRoot from '@/components/ModalRoot'
import DropdownHandler from '@/components/search/DropdownHandler'
import Calendar from '@/components/search/fields/CalendarDropdown'
import StickySearchMixin from '@/components/search/StickySearchMixin'
import * as events from '@/events'
import { offersListUpdated } from '@/events'
import { API, SearchConstraintsRequest } from '@/modules/api'
import ExternalSearchDataAdapter from '@/modules/search/ExternalDataAdapter'
import MediaDetector from '@/utils/MediaDetector'

@Component({
  mixins: [
    StickySearchMixin,
    DropdownHandler,
    DestinationLogic,
    AirportLogic,
    DatesLogic,
    DurationLogic,
    PeopleLogic,
    PricePerPersonLogic,
    BoardStandardLogic,
    HotelStandardLogic,
    OfferTypeLogic,
    AmenitiesLogic,
    SportsEntertainmentLogic,
    HotelCategoriesLogic,
    AttractionsLogic,
    BeachLogic,
    ForChildrenLogic,
    ExternalSearchDataAdapter,
    MediaDetector,
  ],
  components: {
    InputBox,
    DatesInputBox,
    Icon,
    ModalRoot,
    Airport,
    Calendar,
    Destination,
    Duration,
    People,
    PricePerPerson,
    BoardStandard,
    HotelStandard,
    OfferType,
    Amenities,
    SportsEntertainment,
    HotelCategories,
    Attractions,
    Beach,
    ForChildren,
  },
  props: {
    form: {
      type: HTMLFormElement,
      required: true,
    },
    shouldSetListingFilters: {
      type: Boolean,
      required: false,
    },
  },
})
export default class DesktopListing extends Vue {
  dropdowns = {
    destination: false,
    airport: false,
    dates: false,
    duration: false,
    people: false,
    filters: false,
  }
  OfferList = null

  get selectedFiltersCount () {
    return this.selectedBoardStandards.length
      + this.selectedHotelStandards.length
      + this.selectedOfferTypes.length
      + this.selectedAmenities.length
      + this.selectedSportsEntertainments.length
      + this.selectedHotelCategories.length
      + this.selectedAttractions.length
      + this.selectedBeaches.length
      + this.selectedForChildren.length
      + this.selectedPricePerPerson.length
  }

  created () {
    new API()
      .getSearchConstraints(new SearchConstraintsRequest())
      .then(this.readSearchConstraintsResponse)
      .then(() => { if (this.shouldSetListingFilters) this.updateNewSearchFilters() })
      .then(this.updateAvailableValues)
      .then(events.appBeforeInitialize.subscribe(this.setComponentAsSticky))

    window.addEventListener('popstate', this.onPopState)
      
    offersListUpdated.subscribe((e, { component } = e.detail) => {
      this.OfferList = component
    })
  }

  onPopState () {
    window.location.reload()
  }

  getQueryParams () {
    const params = Object.values(this.dx)

    return params.reduce((previous, object) => {
      if (object.queryComponent.length > 0) {
        previous.push(object.queryComponent)
      }
      return previous
    }, []).join('&')
  }

  prepareUrl () {
    return `/api/sitecore/OffersList/LoadMoreOffers?${this.getQueryParams()}`
  }

  modifyUrl () {
    const query = this.getQueryParams()

    history.pushState(null, document.title, `${window.location.origin}/wakacje?${query}`)
  }

  search () {
    this.saveValuesToForm()
    this.form.submitWFFM()

    // 
    // if (typeof this.OfferList === 'undefined') {
    //   this.form.submitWFFM()
    // } else {
    //   this.OfferList.clearOffers()
    //   this.OfferList.loadOffers(this.prepareUrl())
    //   this.modifyUrl()
    //   // this.form.submitWFFM()
    // }
  }

  // PrepareOfferListUrl

  saveValuesToForm () {
    this.dx.destination.data = this.destinations
    this.dx.airport.data = this.airports
    this.dx.dates.data = this.dates.selected
    this.dx.duration.data = this.durations
    this.dx.people.data = this.people

    this.dx.pricePerPerson.data = this.pricePerPerson
    this.dx.boardStandard.data = this.boardStandards
    this.dx.hotelStandard.data = this.hotelStandards
    this.dx.offerType.data = this.offerTypes
    this.dx.amenities.data = this.amenities
    this.dx.sportsEntertainment.data = this.sportsEntertainments
    this.dx.hotelCategories.data = this.hotelCategories
    this.dx.attractions.data = this.attractions
    this.dx.beach.data = this.beaches
    this.dx.forChildren.data = this.forChildren
  }

  updateAvailableValues () {
    this.saveValuesToForm()
    return new API()
      .getSearchConstraints(SearchConstraintsRequest.fromWFFMForm(this.form))
      .then(this.updateSearchConstraintsResponse)
  }

  /**
   * @argument {SearchConstraintsResponse} response
   */
  readSearchConstraintsResponse (response) {
    this.setDatesSelectedOnStart(response.minDate, response.maxDate)
    this.dates.validDates = moment.range(response.minDate, response.maxDate)
    this.markAvailabilityOfListEntries(this.airports, response.airports)
    this.markAvailabilityOfListEntries(this.destinations, response.destinations)
    this.airports = this.airports.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.destinations = this.destinations.sort((d1, d2) => d1.name.localeCompare(d2.name))
    this.people = { ...this.people, checkChildrenBirthday: true }
    this.markAvailabilityOfRadioElements(this.durations.ranges, response.validRanges)
    
    this.markAvailabilityOfRadioElements(this.pricePerPerson.options, response.prices)
    this.offerTypes = this.offerTypes.sort((ot1, ot2) => ot1.name.localeCompare(ot2.name))
    this.amenities = this.amenities.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.sportsEntertainments = this.sportsEntertainments.sort((se1, se2) => se1.name.localeCompare(se2.name))
    this.hotelCategories = this.hotelCategories.sort((se1, se2) => se1.name.localeCompare(se2.name))
    this.attractions = this.attractions.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.beaches = this.beaches.sort((b1, b2) => b1.name.localeCompare(b2.name))
    this.forChildren = this.forChildren.sort((fc1, fc2) => fc1.name.localeCompare(fc2.name))
    this.markAvailabilityOfListEntries(this.boardStandards, response.boardStandards)
    this.boardStandards = this.boardStandards.sort((fc1, fc2) => fc1.name.localeCompare(fc2.name))
  }

  /**
   * @argument {SearchConstraintsResponse} response
   */
  updateSearchConstraintsResponse (response) {
    this.dates.selected = moment.range(moment.max(this.dates.selected.start, response.minDate), moment.min(this.dates.selected.end, response.maxDate))
    this.dates.validDates = moment.range(response.minDate, response.maxDate)
    this.markAvailabilityOfListEntries(this.airports, response.airports)
    this.markAvailabilityOfListEntries(this.destinations, response.destinations)
    this.airports = this.airports.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.destinations = this.destinations.sort((d1, d2) => d1.name.localeCompare(d2.name))
    this.markAvailabilityOfRadioElements(this.durations.ranges, response.validRanges)
    
    this.markAvailabilityOfRadioElements(this.pricePerPerson.options, response.prices)
    this.offerTypes = this.offerTypes.sort((ot1, ot2) => ot1.name.localeCompare(ot2.name))
    this.amenities = this.amenities.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.sportsEntertainments = this.sportsEntertainments.sort((se1, se2) => se1.name.localeCompare(se2.name))
    this.hotelCategories = this.hotelCategories.sort((se1, se2) => se1.name.localeCompare(se2.name))
    this.attractions = this.attractions.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.beaches = this.beaches.sort((b1, b2) => b1.name.localeCompare(b2.name))
    this.forChildren = this.forChildren.sort((fc1, fc2) => fc1.name.localeCompare(fc2.name))
    this.markAvailabilityOfListEntries(this.boardStandards, response.boardStandards)
    this.boardStandards = this.boardStandards.sort((fc1, fc2) => fc1.name.localeCompare(fc2.name))

    this.attractions = this.attractions.filter(item => item.name.toLowerCase() !== 'dodaj auto do kompletu')
  }

  markAvailabilityOfListEntries (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.id)
      if (!item.enabled) item.selected = false
    })
  }

  markAvailabilityOfRadioElements (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.value)
    })
  }

  selectAllFromList (list) {  
    list.forEach(item => {
      if (item.enabled) item.selected = true
    })
  }

  setDatesSelectedOnStart (min, max) {
    const before = moment(this.dates.selected.start).isBefore(min)
    if (before) this.dates.selected = moment.range(min, max)
  }

  updateAndReselectList (listToReselect) {
    this.updateAvailableValues().then(() => {
      if (this.allAirportsSelected && listToReselect == 'airports') this.selectAllFromList(this.airports)      
      if (this.allDestinationsSelected && listToReselect == 'destinations') this.selectAllFromList(this.destinations)      
    })
  }

  deselectRadioList (radioList, value) {
    radioList.selected = value
  }

  /**
   * Business requirement:
   * If we click on 'X' icon in DestinationDropdown or AirportDropdown we need to do few thinks:
   * - clear actual clicked control data
   * - update selected items in opossite control form element *but* only if there is selected checkbox to select all items.
   * If checkbox "allSelected" is selected we need to update it every time we change something in destinations/airports
   * 
   * @param {Array} listToDeselect
   * @param {Array} listToReselect
   */
  clear (listToDeselect, listToReselect) {
    listToDeselect.forEach(item => { item.selected = false })
    this.updateAndReselectList(listToReselect)
  }

  clearFilters () {
    log.debug('DesktopListing.Filters - clear')
    this.boardStandards.forEach(item => { item.selected = false })
    this.hotelStandards.forEach(item => { item.selected = false })
    this.offerTypes.forEach(item => { item.selected = false })
    this.amenities.forEach(item => { item.selected = false })
    this.sportsEntertainments.forEach(item => { item.selected = false })
    this.hotelCategories.forEach(item => { item.selected = false })
    this.attractions.forEach(item => { item.selected = false })
    this.beaches.forEach(item => { item.selected = false })
    this.forChildren.forEach(item => { item.selected = false })
    this.deselectRadioList(this.pricePerPerson, '0:0')
  }
}
</script>

<style lang="scss" scoped>
.search {

  .hotel-category {
    grid-row: 1 / 3;
    grid-column: 4;
  }

  &-listing__wrapper {
    padding: 29px 30px 35px;
    background-color: #005ca9;
  }

  &-controls {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-column-gap: 30px;
    grid-row-gap: 20px;
    justify-content: center;
    align-items: flex-end;

    // calendar inputbox
    div:nth-child(3) {
      min-width: 210px;
    }

    @include media(desktop) {
      grid-template-columns: repeat(3, 220px) 130px 170px 119px;
      grid-column-gap: 20px;
      grid-template-rows: 1fr;
    }
  }

  &__input-box {
    font-family: 'Gotham Book';
    margin-bottom: 0;
    font-weight: 400;
    text-transform: none;
    position: relative;
    color: #3c3939;
  }

  &__input-box--clear {
    font-size: 11px;
    padding: 4px 8px;
    margin-left: -8px;

    &:hover {
      color: #A0D5FF;
    }
  }

  &__submit {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 110px;
    height: 36px;
    background-color: transparent;
    border: 2px solid #fff;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 13px;
    line-height: 13px;
    font-family: 'Gotham';
    margin: 0;
    padding: 7px 7px 7px 10px;
    color: #fff;
    transition: .05s all ease-in-out;
    outline: none;

    &:hover {
      background-color: #009FEA;
      border-color: #009FEA;
    }

    @include media(desktop) {
      margin-left: 13px;
    }

    .figure {
      font-size: 6px;
      transform: rotate(-90deg);
      display: inline-block;
      margin-left: 8px;
    }
  }

  .destination-dropdown {
    width: 615px;
  }

  .calendar-dropdown {
    position: absolute;
    margin-top: 4px;
    right: 0;

    @include media(desktop) {
      right: auto;
    }
  }

  .input-box__dropdown {
    z-index: 101;
  }
}

.search-filters {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 40px;
  background-color: #fff;
  padding: 15px;

  @include media(desktop) {
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 60px;
    grid-row-gap: 28px;
    padding: 30px;
  }

  &--toggle {
    position: absolute;
    left: 50%;
    bottom: 0;
    border: 0;
    padding: 0 10px;
    min-width: 160px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 0 -18px;
    color: #fff;
    background-color: #009FEA;
    font-size: 12px;
    text-transform: uppercase;
    font-family: 'Gotham';
    font-weight: bold;
    transform: translate(-50%, 0);

    .figure {
      font-size: 6px;
      margin-left: 10px;
    }
  }

  &--selected {
    background-color: #FF0069;

    span:first-child {
      margin-left: 5px;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    background-color: #EEEEEE;
    padding: 20px;
    text-align: center;
  }

  &__footer--clear {
    display: flex;
    align-items: center;
    text-transform: uppercase;
    color: #545454;
    font-size: 12px;
    margin-right: 15px;
    cursor: pointer;
    font-family: 'Gotham Book';

    &:hover {
      color: #009FEA;
    }
    
    .figure {
      font-size: 8px;
      margin-right: 6px;
    }
  }

  &__footer--select {
    display: flex;
    align-items: center;
    text-transform: uppercase;
    color: #005CA9;
    border: 2px solid #005CA9;
    padding: 8px 12px 7px;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    transition: .05s all ease-in-out; 

    &:hover {
      background-color: #009FEA;
      border-color: #009FEA;
      color: #fff;
    }

    .figure {
      font-size: 16px;
      height: 8px;
      margin-left: 3px;
    }
  }

  &__group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
    grid-column: 2/4;
    grid-row: 2;
    
    @include media(desktop) {
      display: flex;
      flex-direction: column;
      grid-column: auto;
      grid-row: auto;

      div:not(:last-child) {
        margin-bottom: 40px;
      }
    }
  }

  .hotel-standard {
    grid-column: 1;
    grid-row: 2;

    @include media(desktop) {
      grid-column: auto;
      grid-row: auto;
    }
  }
}

.search-sticky {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;

  &--placeholder {
    height: 168px;

    @include media(desktop) {
      height: 106px;
    }
  }
}
</style>

<style lang="scss">
@import './DesktopSearch.scss';

.page--listing .navbar-fixed-top {
  @include media(tablet) {
    position: relative;
  }
}
.page--listing .static-stage {
  margin-top: 0;
}
.page--listing .stage > div:first-child:not(.static-stage) {
  @include media(desktop) {
    margin-top: 48px;
  } 
}

.search-filters__dialog .modal-dialog__wrapper {
  padding-bottom: 100px;
}

.search-filters .radiolist-item {
  @include media(tablet) {
    padding: 2px 0;

    label {
      padding-left: 2em;
    }
  }
}

.search-filters .checklist-item {
  @include media(tablet) {
    padding: 7px 0;

    > div {
      margin-left: 9px;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/search/DesktopListing.vue